import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUpload,
  faInfoCircle,
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import practiceService from '../../../services/practiceService';
import PageLoadingSpinner from '../../../components/PageLoadingSpinner/PageLoadingSpinner';
import './ImportPatients.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const EXPECTED_COLUMNS = [
  { key: 'name', label: 'Name', aliases: ['name', 'Name'] },
  { key: 'login id', label: 'Login ID (Must be unique)', aliases: ['login id', 'Login ID', 'LoginID'] },
  { key: 'status', label: 'Status', aliases: ['status', 'Status'] },
  { key: 'time zone', label: 'Time Zone', aliases: ['time zone', 'Time Zone', 'TimeZone'] },
  { key: 'birth date', label: 'Birth Date', aliases: ['birth date', 'Birth Date', 'BirthDate'] },
  { key: 'gender', label: 'Gender', aliases: ['gender', 'Gender'] },
  { key: 'home phone', label: 'Home Phone', aliases: ['home phone', 'Home Phone', 'home number(1)', 'HomeNumber1'] },
  { key: 'cell phone', label: 'Cell Phone', aliases: ['cell phone', 'Cell Phone', 'home number(2)', 'HomeNumber2'] },
  { key: 'rpm start date', label: 'RPM Start Date', aliases: ['rpm start date', 'RPM Start Date', 'kick-off date', 'KickOffDate'] },
  { key: 'zip code', label: 'ZIP code', aliases: ['zip code', 'ZIP code', 'ZipCode'] },
  { key: 'serial number', label: 'Serial Number', aliases: ['serial number', 'Serial Number', 'SerialNumber'] }
];

const ImportPatients = ({ isReadOnly = false }) => {
  const { practiceId } = useParams();
  const navigate = useNavigate();

  // Basic Setup
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [caregivers, setCaregivers] = useState([]);

  // Form State
  const [formData, setFormData] = useState({ provider: '', caregiver: '', csvFile: null });
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState('');

  // Client Validation State
  const [foundHeaders, setFoundHeaders] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [backendErrors, setBackendErrors] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (practiceId) fetchProvidersAndCaregivers();
  }, [practiceId]);

  const fetchProvidersAndCaregivers = async () => {
    setIsLoading(true);
    try {
      const [providersRes, caregiversRes] = await Promise.all([
        practiceService.getPracticeProviders(practiceId, { minimal: true }),
        practiceService.getPracticeCaregivers(practiceId, { minimal: true })
      ]);

      let providersData = [];
      if (providersRes?.data?.success && providersRes.data.data) {
        providersData = Array.isArray(providersRes.data.data) ? providersRes.data.data : providersRes.data.data.providers || [];
      } else if (Array.isArray(providersRes?.data)) {
        providersData = providersRes.data;
      }
      setProviders(providersData);

      let caregiversData = [];
      if (caregiversRes?.data?.success && caregiversRes.data.data) {
        caregiversData = Array.isArray(caregiversRes.data.data) ? caregiversRes.data.data : caregiversRes.data.data.caregivers || [];
      } else if (Array.isArray(caregiversRes?.data)) {
        caregiversData = caregiversRes.data;
      }
      setCaregivers(caregiversData);
    } catch (error) {
      toast.error('Failed to load providers and caregivers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setFormData((prev) => ({ ...prev, csvFile: file }));
    setFileName(file.name);
    if (errors.csvFile) setErrors((prev) => ({ ...prev, csvFile: '' }));
    
    // Reset validations
    setFoundHeaders([]);
    setParsedData([]);
    setBackendErrors([]);
    setUploadProgress(0);

    // Parse the file for preview and header matching
    parseFile(file, ext);
  };

  const parseFile = (file, ext) => {
    if (ext === '.csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.meta.fields) processHeadersAndData(results.meta.fields, results.data);
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
        if (rows.length > 0) processHeadersAndData(Object.keys(rows[0]), rows);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const processHeadersAndData = (headers, rows) => {
    const detected = EXPECTED_COLUMNS.filter((col) => 
      headers.some((h) => col.aliases.includes(h.trim().toLowerCase()) || h.trim() === col.label)
    ).map((c) => c.key);
    
    setFoundHeaders(detected);
    
    // Build preview data combining dropdown selections
    setParsedData(rows.slice(0, 5).map(row => {
      // Find case insensitive keys for preview mapping
      const getVal = (aliases) => {
        const key = Object.keys(row).find(k => aliases.includes(k.trim().toLowerCase()) || k.trim() === aliases[0]);
        return key ? row[key] : 'N/A';
      };
      
      return {
        name: getVal(['name']),
        status: getVal(['status']),
        homePhone: getVal(['home phone', 'home number(1)', 'homenumber1']),
        cellPhone: getVal(['cell phone', 'home number(2)', 'homenumber2']),
        rpmStart: getVal(['rpm start date', 'kick-off date', 'kickoffdate'])
      };
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.provider) newErrors.provider = 'Provider is required';
    if (!formData.caregiver) newErrors.caregiver = 'Caregiver is required';
    if (!formData.csvFile) newErrors.csvFile = 'CSV file is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancelClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    const hasAny =
      formData.provider ||
      formData.caregiver ||
      formData.csvFile ||
      fileName ||
      parsedData.length > 0 ||
      foundHeaders.length > 0;

    if (hasAny) {
      setFormData({ provider: '', caregiver: '', csvFile: null });
      setFileName('');
      setParsedData([]);
      setFoundHeaders([]);
      setBackendErrors([]);
      setErrors({});
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (practiceId) {
      navigate(`/practices/${practiceId}/patients`, { replace: true });
    }
  };

  const hasAnythingSelected = Boolean(
    formData.provider || formData.caregiver || formData.csvFile || fileName
  );

  // Determine if it's safe to upload
  const isReadyToUpload = formData.csvFile && !isSubmitting && !isReadOnly;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) {
      toast.warning('Practice is locked. Patient import is not available.');
      return;
    }
    if (!validateForm() || !isReadyToUpload) return;

    setIsSubmitting(true);
    setUploadProgress(0);
    setBackendErrors([]);

    const submitData = new FormData();
    submitData.append('provider', formData.provider);
    submitData.append('caregiver', formData.caregiver);
    submitData.append('csvFile', formData.csvFile);
    submitData.append('practiceId', practiceId);

    try {
      const response = await axios.post(`${API_URL}/practices/${practiceId}/patients/bulk-import`, submitData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      if (response.data?.success) {
        const { created, total, errors, errorDetails = [] } = response.data.data || {};
        
        if (errors === 0 && created > 0) {
          toast.success(`Successfully imported all ${created} patients!`);
          setTimeout(() => navigate(`/practices/${practiceId}/patients`), 1500);
        } else if (errors > 0) {
          const hasDupes = errorDetails.some(err => err.error.toLowerCase().includes('already exists'));
          const msg = hasDupes 
            ? `Duplicate Login ID detected! ${errors} row(s) failed.` 
            : `Failed to upload ${errors} patients due to invalid data.`;
            
          if (created > 0) {
            toast.warning(`Partial Success: ${created} patients imported. ` + msg);
          } else {
            toast.error(msg);
          }
          
          setBackendErrors(errorDetails);
        } else {
          toast.error('The file appears to be empty or unreadable.');
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to import patients. Please check the file.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="import-patients-container">
        <div className="import-patients-card">
          <div className="form-border">
            <h3 className="form-title">Add Bulk Patients</h3>
            <PageLoadingSpinner minHeight="360px" />
          </div>
        </div>
      </div>
    );
  }

  const getProviderName = () => {
    const p = providers.find(x => String(x.id || x.provider_id) === String(formData.provider));
    return p ? `${p.first_name} ${p.last_name}` : 'N/A';
  };

  const getCaregiverName = () => {
    const c = caregivers.find(x => String(x.id || x.caregiver_id) === String(formData.caregiver));
    return c ? `${c.first_name} ${c.last_name}` : 'N/A';
  };

  return (
    <div className="import-patients-container">
      <div className="import-patients-card">
        <div className="form-border">
          <h3 className="form-title">Add Bulk Patients</h3>
          {isReadOnly && (
            <div className="alert alert-warning py-2 mb-3" role="alert">
              This practice is locked. Import and bulk changes are disabled (view only).
            </div>
          )}
          <div className="row mt-2">
            <div className="col-12">
              <form onSubmit={handleSubmit} className="import-form" id="import-patients-form">
                <div className="row align-items-end mb-4">
                  <div className="col-md-4 mb-3 mb-md-0">
                    <label className="form-label">Provider <span className="text-danger">*</span></label>
                    <select
                      className={`form-select ${errors.provider ? 'is-invalid' : ''}`}
                      name="provider"
                      value={formData.provider}
                      onChange={handleInputChange}
                      disabled={isReadOnly}
                    >
                      <option value="">Select Provider</option>
                      {providers.map((p) => (
                        <option key={p.id || p.provider_id} value={p.id || p.provider_id}>{p.first_name} {p.last_name}</option>
                      ))}
                    </select>
                    {errors.provider && <span className="text-danger small">{errors.provider}</span>}
                  </div>
                  
                  <div className="col-md-4 mb-3 mb-md-0">
                    <label className="form-label">Caregiver <span className="text-danger">*</span></label>
                    <select
                      className={`form-select ${errors.caregiver ? 'is-invalid' : ''}`}
                      name="caregiver"
                      value={formData.caregiver}
                      onChange={handleInputChange}
                      disabled={isReadOnly}
                    >
                      <option value="">Select Caregiver</option>
                      {caregivers.map((c) => (
                        <option key={c.id || c.caregiver_id} value={c.id || c.caregiver_id}>{c.first_name} {c.last_name}</option>
                      ))}
                    </select>
                    {errors.caregiver && <span className="text-danger small">{errors.caregiver}</span>}
                  </div>

                  <div className="col-md-4 mb-3 mb-md-0">
                    <label className="form-label">CSV / Excel File</label>
                    <div className="file-input-wrapper">
                      <input type="file" ref={fileInputRef} className="form-control file-input-custom" name="csvFile" accept=".csv,.xls,.xlsx" onChange={handleFileChange} disabled={isReadOnly} />
                      <span className="file-upload-button"><FontAwesomeIcon icon={faCloudUpload} className="me-2"/>Upload CSV/Excel</span>
                      <span className="file-label text-truncate ms-2">{fileName || 'No file chosen'}</span>
                    </div>
                    {errors.csvFile && <span className="text-danger small">{errors.csvFile}</span>}
                  </div>
                </div>

                {/* Upload Progress Bar */}
                {isSubmitting && (
                  <div className="progress mt-2" style={{ height: '8px' }}>
                    <div className="progress-bar progress-bar-striped progress-bar-animated bg-success" role="progressbar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                {/* Patient Error / Duplicate IDs Alert */}
                {backendErrors.length > 0 && (
                  <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small border-warning shadow-sm" style={{ backgroundColor: '#fff3cd' }}>
                    <strong className="text-dark"><FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning"/> Failed to Upload:</strong>
                    <ul className="mb-0 mt-1 ps-4 text-secondary" style={{ listStyleType: 'circle' }}>
                      {backendErrors.map((err, idx) => (
                        <li key={idx}>
                          <span className="fw-bold text-dark">{err.username || `Row ${err.row}`}</span> 
                          <span className="ms-1">- {err.error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </form>
                <div className="row mb-3 mt-3">
                  <div className="col-12 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary cancel-btn-custom me-2"
                      onClick={handleCancelClick}
                      style={{ padding: '8px 16px' }}
                      disabled={!hasAnythingSelected}
                      title={!hasAnythingSelected ? 'Select provider, caregiver, or a file to enable Cancel' : undefined}
                    >
                      Cancel
                    </button>
                    <button type="submit" form="import-patients-form" className="btn btn-import-custom" disabled={!isReadyToUpload || isReadOnly}>
                      {isSubmitting ? <><FontAwesomeIcon icon={faSpinner} spin className="me-2"/> {uploadProgress}%</> : 'Import Patients'}
                    </button>
                  </div>
                </div>
            </div>
          </div>

          {/* Data Preview Table */}
          {parsedData.length > 0 && formData.csvFile && (
            <div className="mt-4 preview-section">
              <h6 className="text-muted mb-3"><FontAwesomeIcon icon={faInfoCircle} className="me-2"/> Data Entry Preview (First {parsedData.length} records)</h6>
              <div className="table-responsive">
                <table className="table table-bordered table-striped preview-table text-sm">
                  <thead className="table-dark">
                    <tr>
                      <th>Status</th>
                      <th>Name</th>
                      <th>Home Phone</th>
                      <th>Cell Phone</th>
                      <th>RPM Start Date</th>
                      <th>Provider</th>
                      <th>Caregiver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.map((row, idx) => (
                      <tr key={idx}>
                        <td><span className={`badge ${row.status?.toLowerCase() === 'active' ? 'bg-success' : 'bg-secondary'}`}>{row.status || 'Active'}</span></td>
                        <td>{row.name}</td>
                        <td>{row.homePhone}</td>
                        <td>{row.cellPhone}</td>
                        <td>{row.rpmStart}</td>
                        <td className="text-muted">{getProviderName()}</td>
                        <td className="text-muted">{getCaregiverName()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ImportPatients;
